import type {
	CommandConstructor,
	CommandProperties,
} from '@atlassian/rovo-content-bridge-api/command';
import { createCommand } from '@atlassian/rovo-content-bridge-api/command-creator';

/**
 * The placement describes where/how the content lands in the editor.
 * - `'at-cursor'` — insert at the current cursor position (default editor behaviour)
 * - `'before-block'` — insert before the current block node
 * - `'after-block'` — insert after the current block node
 * - `'replace-selection'` — replace the current selection with the content
 * - `'end-of-doc'` — append at the very end of the document
 */
export type EditorInsertContentPlacement =
	| 'at-cursor'
	| 'before-block'
	| 'after-block'
	| 'replace-selection'
	| 'end-of-doc';

/**
 * `data` mirrors the document format convention used in other bridge API commands.
 * `placement` is present on all content variants and describes where content lands.
 */
export type EditorInsertContentPresentation = {
	layout?: 'center' | 'align-start' | 'align-end' | 'wide' | 'full-width';
	width: number;
	height: number;
	widthType?: 'pixel';
};

export type EditorInsertContentCommandAction =
	| {
			mode: 'insert';
			placement: EditorInsertContentPlacement;
			useStoredSelection?: boolean;
			data: {
				type: 'text/markdown';
				content: string;
			};
	  }
	| {
			mode: 'insert';
			placement: EditorInsertContentPlacement;
			restoreSelection?: boolean;
			// `object` to avoid adding '@atlaskit/adf-schema' as dependency
			data: {
				type: 'text/adf';
				content: object;
			};
	  }
	| {
			mode: 'upload-and-insert-media';
			placement: EditorInsertContentPlacement;
			useStoredSelection?: boolean;
			/** When true, shows the AI-generating overlay during upload. Defaults to no overlay. */
			isAIGenerated?: boolean;
			/**
			 * The interaction source that triggered this insert, used to derive the
			 * inputMethod on the document inserted analytics event.
			 */
			interactionSource?: string;
			/** Aspect ratio of the AI-generated image (e.g. "16:9", "1:1"); used by the receiver to size the inserted media. */
			aspectRatio?: string | null;
			/** Specific to Remix content - the dynamicUiSubtype (e.g. "corporate-doodle", "image"). */
			remixSubtype?: string;
			/**
			 * Generated alt text for the inserted image (max 510 chars). A non-blank string sets the media node's alt.
			 * Missing, null or empty means 'no alt': the image is inserted without alt. With `replace-selection`,
			 * the replaced image's alt is never carried over.
			 */
			altText?: string | null;
			data: {
				// Can be narrowed down to mime types later
				type: string;
				url: string;
				presentation?: EditorInsertContentPresentation;
			};
	  };

/**
 * Optional metadata about the originating conversation/request, propagated end-to-end
 * so the receiver can attach correlation ids to its own analytics/error reporting.
 *
 * Bridge-driven inserts (e.g. Rovo chat → editor) have no shared React context, so
 * relying on `useAnalyticsPropertiesContext` on the editor side is unreliable — pass
 * the values explicitly via the command payload instead.
 */
export type EditorInsertContentCommandMeta = {
	/**
	 * Conversation channel id of the chat session that triggered this insert.
	 * Should be populated by senders that have it available (e.g. via
	 * `useAnalyticsPropertiesContext` on the chat side).
	 */
	conversationChannelId?: string;
	/**
	 * Requests a positive result only after the editor produces the insertion transaction.
	 * Omit this for legacy fire-and-forget behavior.
	 */
	requireInsertionAcknowledgement?: boolean;
};

/**
 * Properties for the generic editor insert content command.
 */
export type EditorInsertContentCommandProperties = CommandProperties &
	EditorInsertContentCommandAction &
	EditorInsertContentCommandMeta;

/**
 * Positive acknowledgement that the editor produced and dispatched the requested
 * insertion transaction. Media insertions additionally return their stable file id.
 */
export type EditorInsertContentCommandResult =
	| {
			inserted: true;
			mediaFileId?: string;
	  }
	| {
			/** Compatibility shape accepted from existing media insertion handlers. */
			inserted?: never;
			mediaFileId: string;
	  }
	| undefined;

/**
 * A generic command sent by a bridge API client to insert or replace content in an editor.
 *
 * @summary {@link CommandProperties.senderId} is requesting {@link CommandProperties.receiverId}
 * to insert or replace content in the editor at the current cursor position or selection.
 *
 * @description This is the bridge API replacement for the legacy `usePublish` + `editor-suggestion`
 * pub/sub pattern from `@atlaskit/rovo-triggers`. Prefer this command for all new integrations.
 */
export const EditorInsertContentCommand: CommandConstructor<
	EditorInsertContentCommandProperties,
	EditorInsertContentCommandResult
> = createCommand<EditorInsertContentCommandProperties, EditorInsertContentCommandResult>(
	'editor-insert-content-command',
);
